import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Download,
  SkipBack,
  SkipForward,
  Gauge,
  PictureInPicture2,
} from 'lucide-react';
import {
  ActionMenu,
  IconButton,
  Slider,
  Tooltip,
} from '@ntconsult/aluma-react';
import { cn } from '@common/utils/cn';
import { formatDuration } from '@common/utils/format';
import { PLAYBACK_RATES, type PlaybackRate } from '../hooks/useVideoPlayer';

interface VideoControlsProps {
  t: (key: string, vars?: Record<string, unknown>) => string;
  showControls: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  isFullscreen: boolean;
  isPip: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playbackRate: PlaybackRate;
  showTimeAsRemaining: boolean;
  pipSupported: boolean;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onToggleFullscreen: () => void;
  onTogglePip: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (value: number) => void;
  onSkip: (seconds: number) => void;
  onDownload: () => void;
  onChangeRate: (rate: PlaybackRate) => void;
  onToggleTimeDisplay: () => void;
}

export function VideoControls({
  t,
  showControls,
  isPlaying,
  isMuted,
  isFullscreen,
  isPip,
  currentTime,
  duration,
  volume,
  playbackRate,
  showTimeAsRemaining,
  pipSupported,
  onTogglePlay,
  onToggleMute,
  onToggleFullscreen,
  onTogglePip,
  onSeek,
  onVolumeChange,
  onSkip,
  onDownload,
  onChangeRate,
  onToggleTimeDisplay,
}: VideoControlsProps) {
  const safeDuration = duration > 0 ? duration : 0;
  const displayTime = showTimeAsRemaining
    ? `-${formatDuration(Math.max(0, Math.floor(safeDuration - currentTime)))}`
    : formatDuration(Math.floor(currentTime));

  return (
    <div
      className={cn(
        'dark absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity duration-200',
        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
    >
      <Slider
        value={[currentTime]}
        min={0}
        max={safeDuration || 100}
        step={0.1}
        onValueChange={([v]) => onSeek(v)}
        ariaLabel={t('player.shortcuts')}
        formatValue={(v) => formatDuration(Math.floor(v))}
        className="mb-2"
      />

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Tooltip content={t('player.skipBack')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={t('player.skipBack')}
              icon={<SkipBack className="w-4 h-4" aria-hidden="true" />}
              onClick={() => onSkip(-10)}
            />
          </Tooltip>

          <Tooltip content={isPlaying ? t('player.pause') : t('player.play')}>
            <IconButton
              variant="primary"
              size="md"
              label={isPlaying ? t('player.pause') : t('player.play')}
              icon={
                isPlaying ? (
                  <Pause className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Play className="w-5 h-5" aria-hidden="true" />
                )
              }
              onClick={onTogglePlay}
            />
          </Tooltip>

          <Tooltip content={t('player.skipForward')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={t('player.skipForward')}
              icon={<SkipForward className="w-4 h-4" aria-hidden="true" />}
              onClick={() => onSkip(10)}
            />
          </Tooltip>

          <div className="flex items-center gap-1 ml-1">
            <Tooltip content={isMuted ? t('player.unmute') : t('player.mute')}>
              <IconButton
                variant="ghost"
                size="sm"
                label={isMuted ? t('player.unmute') : t('player.mute')}
                icon={
                  isMuted || volume === 0 ? (
                    <VolumeX className="w-4 h-4" aria-hidden="true" />
                  ) : (
                    <Volume2 className="w-4 h-4" aria-hidden="true" />
                  )
                }
                onClick={onToggleMute}
              />
            </Tooltip>
            <div className="w-20">
              <Slider
                value={[isMuted ? 0 : volume]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([v]) => onVolumeChange(v)}
                ariaLabel={t('player.mute')}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={onToggleTimeDisplay}
            className="ml-2 text-xs tabular-nums text-foreground-muted hover:text-foreground transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 rounded px-1"
          >
            <span className="text-foreground">{displayTime}</span>
            <span className="opacity-50"> / {formatDuration(Math.floor(safeDuration))}</span>
          </button>
        </div>

        <div className="flex items-center gap-1">
          <ActionMenu
            align="right"
            side="top"
            trigger={
              <IconButton
                variant="ghost"
                size="sm"
                label={`${t('player.speed')} ${playbackRate}x`}
                icon={<Gauge className="w-4 h-4" aria-hidden="true" />}
              />
            }
          >
            {PLAYBACK_RATES.map((rate) => (
              <ActionMenu.Item key={rate} onClick={() => onChangeRate(rate)}>
                <span className={cn('tabular-nums', rate === playbackRate && 'font-semibold text-brand-primary')}>
                  {rate}x{rate === 1 ? ` · ${t('player.speed')}` : ''}
                </span>
              </ActionMenu.Item>
            ))}
          </ActionMenu>

          {pipSupported && (
            <Tooltip content={isPip ? t('player.exitPip') : t('player.pip')}>
              <IconButton
                variant="ghost"
                size="sm"
                label={isPip ? t('player.exitPip') : t('player.pip')}
                icon={<PictureInPicture2 className="w-4 h-4" aria-hidden="true" />}
                onClick={onTogglePip}
              />
            </Tooltip>
          )}

          <Tooltip content={t('player.download')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={t('player.download')}
              icon={<Download className="w-4 h-4" aria-hidden="true" />}
              onClick={onDownload}
            />
          </Tooltip>

          <Tooltip content={isFullscreen ? t('player.exitFullscreen') : t('player.fullscreen')}>
            <IconButton
              variant="ghost"
              size="sm"
              label={isFullscreen ? t('player.exitFullscreen') : t('player.fullscreen')}
              icon={
                isFullscreen ? (
                  <Minimize className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Maximize className="w-4 h-4" aria-hidden="true" />
                )
              }
              onClick={onToggleFullscreen}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
